/**
 * @file AllCategories.tsx
 * @deprecated Copied to app/modules/procurement/pages/master/category/AllCategories.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/master/category/AllCategories
 * @description Main page for managing categories in UProcure master data.
 * Displays a hierarchical list of primary, secondary, and group categories with
 * expandable sections. Provides add, edit, and delete functionality based on user
 * permissions. Uses delete confirmation modals and persists scroll position on navigation.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Warehouse refetch: fetchCategory runs on mount only; refetch when selectedWarehouse
 *   changes so list/delete reflect the active warehouse context.
 * @todo Duplicate page titles: uProcure/master/index.tsx uses PageTitle on routes; this component
 *   also renders h1 — remove one.
 * @todo Route-level permission guard: component uses history.back() on mount only; add
 *   Categories permission check in PrivateRoutes or route wrapper if required.
 * @todo Unused import: generateLogMessageForCategory is imported but never used — wire into
 *   delete handlers or remove import.
 * @todo uProcure parity gaps: edit-sub-categories missing useDebounce on primary-category
 *   search (uprocure-admin uses 500ms); edit-group-categories has debug console.log to remove.
 */

import clsx from "clsx";
import { useEffect, useState } from "react";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../../../../../Global/loader";
import APICallService from "../../../../../api/apiCallService";
import { master } from "../../../../../api/apiEndPoints";
import { CustomSelectBlue } from "../../../../custom/Select/CustomSelectBlue";
import ThreeDotMenu from "../../../../../_admin/assets/media/svg_uTrade/three-dot.svg";
import { CustomSelectTable } from "../../../../custom/Select/CustomSelectTable";
import { success } from "../../../../../Global/toast";
import { masterToast } from "../../../../../utils/toast";
import DeleteModal from "../../../../modals/delete";
import { listCategories } from "../../../../../utils/storeString";
import { getKey, setKey } from "../../../../../Global/history";
import { ElementAnimateUtil } from "../../../../../_admin/assets/ts/_utils";
import Method from "../../../../../utils/methods";
import { Add, Categories, Delete, Edit } from "../../../../../utils/constants";
import { useAuth } from "../../../auth";
// import { warehouse as warehouseStoreString } from '../../../../utils/storeString';
import { useWarehouse } from "../../../../context/WarehouseContext";
import ImageWithFallback from "../../../_common/components/image-with-fallback";

const AllCategories = () => {
  const { selectedWarehouse } = useWarehouse();
  const depth = 3;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [id, setId] = useState("");
  const [subId, setSubId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [deletePrimaryModalOpen, setDeletePrimaryModalOpen] = useState(false);
  const [selectedPrimary, setSelectedPrimary] = useState<any>();
  const [primaryId, setPrimaryId] = useState("");

  const [deleteSecondaryModalOpen, setDeleteSecondaryModalOpen] =
    useState(false);
  const [selectedSecondary, setSelectedSecondary] = useState<any>();
  const [secondaryId, setSecondaryId] = useState("");

  const [deleteGroupModalOpen, setDeleteGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(Categories, currentUser)) {
        return window.history.back();
      }
      await fetchCategory(depth);
    })();
  }, []);

  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(listCategories.scrollPosition);
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
      }
    }
  }, [dataFetched]);

  const fetchCategory = async (categoriesDepth: number) => {
    setLoading(true);
    let params: any = {
      sortKey: "title",
      sortOrder: 1,
      categoriesDepth: categoriesDepth,
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }
    let apiService = new APICallService(
      master.categoryList,
      params,
      "",
      "",
      "",
      "",
      Categories,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setCategory(response.records);
    }
    setDataFetched(true);
    setLoading(false);
  };
  const openMenuOnClick = async (id: any) => {
    setId(id);
  };
  const onMenuClose = async () => {
    setId("");
  };
  const onMenuOpen = async (id: any) => {
    setId(id);
  };
  const handlePrimaryOption = async (event: any, data: any) => {
    let currentScrollY = window.scrollY;
    setKey(listCategories.scrollPosition, currentScrollY);
    if (event.value === 1) {
      data["categories"] = data["categories"].map((val: any) => {
        return {
          label: val.title,
          value: val.title,
          _id: val._id,
        };
      });
      navigate("/UProcure/master/edit-primary-categories", {
        state: data,
      });
    } else if (event.value === 2) {
      setDeletePrimaryModalOpen(true);
      setSelectedPrimary(data);
    } else if (event.value === 0) {
    }
  };

  const handlePrimaryDelete = async () => {
    setDeleteLoader(true);
    let apiService = new APICallService(
      master.deleteCategory,
      selectedPrimary?._id,
      "",
      "",
      "",
      true,
      Categories,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.error) {
      setError(response.error);
    } else {
      fetchCategory(depth);
      success(masterToast.deletePrimaryCategory);
      setDeletePrimaryModalOpen(false);
    }
    setDeleteLoader(false);
  };

  const handlePrimaryModalHide = () => {
    setDeletePrimaryModalOpen(false);
    setError("");
  };

  const openSecondaryMenuOnClick = async (id: any) => {
    setSubId(id);
  };
  const onSecondaryMenuClose = async () => {
    setSubId("");
  };
  const onSecondaryMenuOpen = async (id: any) => {
    setSubId(id);
  };
  const handleSecondaryOption = async (
    event: any,
    data: any,
    primaryVal: any,
  ) => {
    let currentScrollY = window.scrollY;
    setKey(listCategories.scrollPosition, currentScrollY);
    if (event.value === 1) {
      data["primaryCategories"] = {
        image: primaryVal.image,
        title: primaryVal.title,
        _id: primaryVal._id,
      };
      navigate("/UProcure/master/edit-sub-categories", {
        state: data,
      });
    } else if (event.value === 2) {
      setDeleteSecondaryModalOpen(true);
      setSelectedSecondary(data);
      setPrimaryId(primaryVal._id);
    } else if (event.value === 0) {
    }
  };

  const handleSecondaryDelete = async () => {
    let subCategoryId = selectedSecondary?._id;
    setDeleteLoader(true);
    let apiService = new APICallService(
      master.deleteSubCategory,
      "",
      { primaryId, subCategoryId },
      "",
      "",
      true,
      Categories,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.error) {
      setError(response.error);
    } else {
      fetchCategory(depth);
      success(masterToast.deleteSubCategory);
      setDeleteSecondaryModalOpen(false);
    }
    setDeleteLoader(false);
  };

  const handleSecondaryModalHide = () => {
    setDeleteSecondaryModalOpen(false);
    setError("");
  };

  const openGroupMenuOnClick = async (id: any) => {
    setGroupId(id);
  };
  const onGroupMenuClose = async () => {
    setGroupId("");
  };
  const onGroupMenuOpen = async (id: any) => {
    setGroupId(id);
  };
  const handleGroupOption = async (
    event: any,
    data: any,
    primaryCategory: any,
    subCategory: any,
  ) => {
    let currentScrollY = window.scrollY;
    setKey(listCategories.scrollPosition, currentScrollY);
    if (event.value === 1) {
      data["primaryCategory"] = {
        image: primaryCategory.image,
        title: primaryCategory.title,
        _id: primaryCategory._id,
      };
      data["subCategory"] = {
        image: subCategory.image,
        title: subCategory.title,
        _id: subCategory._id,
      };
      navigate("/UProcure/master/edit-group-categories", {
        state: data,
      });
    } else if (event.value === 2) {
      setDeleteGroupModalOpen(true);
      setSelectedGroup(data);
      setPrimaryId(primaryCategory._id);
      setSecondaryId(subCategory._id);
    } else if (event.value === 0) {
    }
  };

  const handleGroupDelete = async () => {
    let groupId = selectedGroup?._id;
    setDeleteLoader(true);
    let apiService = new APICallService(
      master.deleteGroupCategory,
      "",
      { primaryId, secondaryId, groupId },
      "",
      "",
      true,
      Categories,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.error) {
      setError(response.error);
    } else {
      fetchCategory(depth);
      success(masterToast.deleteGroupCategory);
      setDeleteGroupModalOpen(false);
    }
    setDeleteLoader(false);
  };

  const handleGroupModalHide = () => {
    setDeleteGroupModalOpen(false);
    setError("");
  };

  const handleChange = (event: any) => {
    if (event.value === "0") {
      navigate("/UProcure/master/add-primary-categories");
    }
    if (event.value === "1") {
      navigate("/UProcure/master/add-sub-categories");
    }
    if (event.value === "2") {
      navigate("/UProcure/master/add-group-categories");
    }
  };
  return (
    <>
      {deletePrimaryModalOpen && (
        <DeleteModal
          show={deletePrimaryModalOpen}
          onHide={handlePrimaryModalHide}
          service={selectedPrimary}
          handleDelete={handlePrimaryDelete}
          error={error}
          loading={deleteLoader}
          serviceName="category"
          message={`Are you sure you want to delete ${selectedPrimary.title} category?`}
          warnMessage={`By continuing, the ${selectedPrimary.title} category will
          be removed permanently`}
        />
      )}
      {deleteSecondaryModalOpen && (
        <DeleteModal
          show={deleteSecondaryModalOpen}
          onHide={handleSecondaryModalHide}
          service={selectedSecondary}
          handleDelete={handleSecondaryDelete}
          error={error}
          loading={deleteLoader}
          serviceName="subcategory"
          message={`Are you sure you want to delete ${selectedSecondary.title} subcategory?`}
          warnMessage={`By continuing, the ${selectedSecondary.title} subcategory will
          be removed permanently`}
        />
      )}
      {deleteGroupModalOpen && (
        <DeleteModal
          show={deleteGroupModalOpen}
          onHide={handleGroupModalHide}
          service={selectedGroup}
          handleDelete={handleGroupDelete}
          error={error}
          loading={deleteLoader}
          serviceName="groupcategory"
          message={`Are you sure you want to delete ${selectedGroup.title} groupcategory?`}
          warnMessage={`By continuing, the ${selectedGroup.title} groupcategory will
          be removed permanently`}
        />
      )}
      <div className="p-5">
        <Row className="align-items-center mb-5">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-0">Category</h1>
          </Col>
          {loading ? (
            <></>
          ) : category && category.length > 0 ? (
            <Col sm="auto">
              <div className="border border-r10px w-fit-content text-white">
                {/* {Method.hasPermission(Categories, Add, currentUser) ? (
                  <CustomSelectBlue
                    isSearchable={false}
                    menuRight={true}
                    placeholder={
                      <>
                        <span className="fs-16 fw-bolder text-white">Add</span>
                      </>
                    }
                    default={{
                      value: "AddPrimaryCategoryDefault",
                      name: "AddPrimaryCategoryDefault",
                      label: (
                        <>
                          <span className="fs-16 fw-600 ">
                            Add primary category
                          </span>
                        </>
                      ),
                    }}
                    options={[
                      {
                        value: "0",
                        name: "AddPrimaryCategory",
                        label: (
                          <>
                            <Link
                              to="/UProcure/master/add-primary-categories"
                              className="text-active-white text-black"
                            >
                              <span>Primary category</span>
                            </Link>
                          </>
                        ),
                      },
                      {
                        value: "1",
                        name: "AddSubCategory",
                        label: (
                          <>
                            <Link
                              to="/UProcure/master/add-sub-categories"
                              className="text-active-white text-black"
                            >
                              <span>Sub category</span>
                            </Link>
                          </>
                        ),
                      },
                      {
                        value: "2",
                        name: "AddGroupCategory",
                        label: (
                          <>
                            <Link
                              to="/UProcure/master/add-group-categories"
                              className="text-active-white text-black"
                            >
                              <span>Group category</span>
                            </Link>
                          </>
                        ),
                      },
                    ]}
                    isMulti={false}
                    onChange={handleChange}
                  />
                ) : (
                  <></>
                )} */}
              </div>
            </Col>
          ) : (
            <>
              <Col lg={12} className="mt-5">
                {Method.hasPermission(Categories, Add, currentUser) ? (
                  <div className="border border-r10px p-md-9 p-7">
                    <h2 className="fs-22 fw-bolder">
                      Start adding categories!
                    </h2>
                    <p className="fs-18 fw-500">
                      You can add primary categories, sub categories and group
                      categories.
                    </p>
                    <Link
                      to="/UProcure/master/add-primary-categories"
                      className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary btn-lg fs-16 fw-600"
                    >
                      Primary category
                    </Link>
                  </div>
                ) : (
                  <></>
                )}
              </Col>
            </>
          )}
        </Row>
        {loading ? (
          <>
            <div className="d-flex justify-content-center m-4 align-items-center h-200px">
              <Loader loading={loading} />
            </div>
          </>
        ) : (
          <>
            {category.length > 0 ? (
              <>
                <Card className="card-custom categories-card border border-r10px">
                  <Card.Header className="min-h-65px">
                    <h3 className="card-title fs-16 fw-600">All categories</h3>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {!loading ? (
                      category.length > 0 &&
                      category.map((primaryVal: any, primaryIndex: number) => {
                        return (
                          <>
                            <div
                              className="accordion"
                              id={"kt_accordion_" + primaryVal._id}
                            >
                              <div className="accordion-item">
                                <div
                                  className={clsx(
                                    primaryVal.categories.length
                                      ? "accordion-header"
                                      : " accordion-header accordion-header-white",
                                  )}
                                  id={"accordion_" + primaryVal._id}
                                >
                                  <Row className="align-items-center p-0">
                                    <Col xs className="pe-0">
                                      <button
                                        // className="accordion-button fs-16 fw-600 pe-0 border-bottom-0 collapsed"
                                        className={clsx(
                                          primaryVal.categories.length
                                            ? "accordion-button"
                                            : "accordion-button accordion-button-white",
                                          "fs-16 fw-600 pe-0 border-bottom-0 collapsed",
                                        )}
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target={
                                          "#body_accordion_" + primaryVal._id
                                        }
                                        aria-expanded="false"
                                        aria-controls={
                                          "body_accordion_" + primaryVal._id
                                        }
                                      >
                                        <span className="symbol symbol-35px border bg-body border-r8px me-3">
                                          <ImageWithFallback
                                            className="img-fluid"
                                            src={primaryVal.image}
                                            alt=""
                                          />
                                        </span>
                                        <span className="w-100">
                                          {primaryVal.title}
                                        </span>
                                      </button>
                                    </Col>
                                    <Col xs="auto">
                                      <div className="my-0 bg-light float-end me-5">
                                       {Method.hasPermission(
                                          Categories,
                                          Edit,
                                          currentUser,
                                        ) ||
                                        Method.hasPermission(
                                          Categories,
                                          Delete,
                                          currentUser,
                                        ) ? (
                                          <CustomSelectTable
                                            marginLeft={"-90px"}
                                            placeholder={
                                              <img
                                                className="me-3"
                                                width={24}
                                                height={5}
                                                src={ThreeDotMenu}
                                                alt=""
                                              />
                                            }
                                            backgroundColor={
                                              primaryVal.categories.length
                                                ? "#f9f9f9"
                                                : "#fff"
                                            }
                                            options={[
                                              ...(Method.hasPermission(
                                                Categories,
                                                Edit,
                                                currentUser,
                                              )
                                                ? [
                                                    {
                                                      label: (
                                                        <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                          Edit
                                                        </button>
                                                      ),
                                                      value: 1,
                                                    },
                                                  ]
                                                : []),
                                              ...(Method.hasPermission(
                                                Categories,
                                                Delete,
                                                currentUser,
                                              )
                                                ? [
                                                    {
                                                      label: (
                                                        <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4">
                                                          Delete
                                                        </button>
                                                      ),
                                                      value: 2,
                                                    },
                                                  ]
                                                : []),
                                            ]}
                                            show={primaryVal._id === id}
                                            onMenuClose={() => {
                                              onMenuClose();
                                            }}
                                            openMenuOnClick={() => {
                                              openMenuOnClick(primaryVal._id);
                                            }}
                                            onMenuOpen={() => {
                                              onMenuOpen(primaryVal._id);
                                            }}
                                            onChange={(event: any) => {
                                              handlePrimaryOption(
                                                event,
                                                primaryVal,
                                              );
                                            }}
                                            isOptionDisabled={(option: any) =>
                                              option.value === 3
                                            }
                                          />
                                        ) : (
                                          <></>
                                        )}
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                                {primaryVal.categories.length > 0 &&
                                  primaryVal.categories.map(
                                    (subVal: any, subIndex: number) => {
                                      return (
                                        <>
                                          <div
                                            id={
                                              "body_accordion_" + primaryVal._id
                                            }
                                            className="accordion-collapse collapse"
                                            aria-labelledby={
                                              "accordion_" + subVal._id
                                            }
                                            data-bs-parent={
                                              "#kt_accordion_" + subVal._id
                                            }
                                          >
                                            <div className="accordion-body p-0">
                                              <div className="accordion-item">
                                                {/* {subVal.categories.length > 0 ?} */}
                                                <div
                                                  className={clsx(
                                                    subVal.categories.length
                                                      ? "accordion-header"
                                                      : " accordion-header accordion-header-white",
                                                  )}
                                                  id={
                                                    "accordion_primary_" +
                                                    subVal._id
                                                  }
                                                >
                                                  <Row className="align-items-center p-0">
                                                    <Col xs>
                                                      <button
                                                        className={clsx(
                                                          subVal.categories
                                                            .length
                                                            ? "accordion-button"
                                                            : "accordion-button accordion-button-white",
                                                          "fs-16 fw-600 pe-0 border-top-0 border-bottom-0 ps-18 collapsed",
                                                        )}
                                                        type="button"
                                                        data-bs-toggle="collapse"
                                                        data-bs-target={
                                                          "#body_accordion_primary_" +
                                                          subVal._id
                                                        }
                                                        aria-expanded="false"
                                                        aria-controls={
                                                          "body_accordion_primary_" +
                                                          subVal._id
                                                        }
                                                      >
                                                        <span className="symbol symbol-35px border bg-body border-r8px me-3">
                                                          <ImageWithFallback
                                                            className="img-fluid"
                                                            src={subVal.image}
                                                            alt=""
                                                          />
                                                        </span>
                                                        <span className="w-100">
                                                          {subVal.title}
                                                        </span>
                                                      </button>
                                                    </Col>
                                                    <Col
                                                      xs="auto"
                                                      className="ps-0"
                                                    >
                                                      {/* begin::Menu */}
                                                      <div className="my-0 float-end me-5">
                                                       {Method.hasPermission(
                                                          Categories,
                                                          Edit,
                                                          currentUser,
                                                        ) ||
                                                        Method.hasPermission(
                                                          Categories,
                                                          Delete,
                                                          currentUser,
                                                        ) ? (
                                                          <CustomSelectTable
                                                            marginLeft={"-90px"}
                                                            placeholder={
                                                              <img
                                                                className="me-3"
                                                                width={24}
                                                                height={5}
                                                                src={
                                                                  ThreeDotMenu
                                                                }
                                                                alt=""
                                                              />
                                                            }
                                                            backgroundColor={
                                                              subVal.categories
                                                                .length
                                                                ? "#f9f9f9"
                                                                : "#fff"
                                                            }
                                                            options={[
                                                              ...(Method.hasPermission(
                                                                Categories,
                                                                Edit,
                                                                currentUser,
                                                              )
                                                                ? [
                                                                    {
                                                                      label: (
                                                                        <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                                          Edit
                                                                          details
                                                                        </button>
                                                                      ),
                                                                      value: 1,
                                                                    },
                                                                  ]
                                                                : []),
                                                              ...(Method.hasPermission(
                                                                Categories,
                                                                Delete,
                                                                currentUser,
                                                              )
                                                                ? [
                                                                    {
                                                                      label: (
                                                                        <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4">
                                                                          Delete
                                                                          details
                                                                        </button>
                                                                      ),
                                                                      value: 2,
                                                                    },
                                                                  ]
                                                                : []),
                                                            ]}
                                                            show={
                                                              subVal._id ===
                                                              subId
                                                            }
                                                            onMenuClose={() => {
                                                              onSecondaryMenuClose();
                                                            }}
                                                            openMenuOnClick={() => {
                                                              openSecondaryMenuOnClick(
                                                                subVal._id,
                                                              );
                                                            }}
                                                            onMenuOpen={() => {
                                                              onSecondaryMenuOpen(
                                                                subVal._id,
                                                              );
                                                            }}
                                                            onChange={(
                                                              event: any,
                                                            ) => {
                                                              handleSecondaryOption(
                                                                event,
                                                                subVal,
                                                                primaryVal,
                                                              );
                                                            }}
                                                            isOptionDisabled={(
                                                              option: any,
                                                            ) =>
                                                              option.value === 3
                                                            }
                                                          />
                                                        ) : (
                                                          <></>
                                                        )}
                                                      </div>
                                                      {/* end::Menu */}
                                                    </Col>
                                                  </Row>
                                                </div>
                                                {subVal.categories.length > 0 &&
                                                  subVal.categories.map(
                                                    (
                                                      groupVal: any,
                                                      groupIndex: number,
                                                    ) => {
                                                      return (
                                                        <>
                                                          <div
                                                            id={
                                                              "body_accordion_primary_" +
                                                              subVal._id
                                                            }
                                                            className="accordion-collapse collapse"
                                                            aria-labelledby={
                                                              "accordion_primary_" +
                                                              subVal._id
                                                            }
                                                            data-bs-parent={
                                                              "#kt_accordion_primary_" +
                                                              subVal._id
                                                            }
                                                          >
                                                            <div
                                                              className={clsx(
                                                                "accordion-body border-bottom ps-20 p-0 ",
                                                              )}
                                                            >
                                                              <div
                                                                className={clsx(
                                                                  "d-flex justify-content-between h-58px pe-3",
                                                                )}
                                                              >
                                                                <div className="d-flex align-items-center fs-16 fw-600">
                                                                  <div className="symbol symbol-35px">
                                                                    <div className="symbol-label bg-body border fs-16 fw-600 text-gray me-3">
                                                                      {groupIndex +
                                                                        1}
                                                                    </div>
                                                                  </div>
                                                                  <span className="w-100">
                                                                    {
                                                                      groupVal.title
                                                                    }
                                                                  </span>
                                                                </div>
                                                                <div className="d-flex align-items-center fs-16 fw-600 me-5">
                                                                 {Method.hasPermission(
                                                                    Categories,
                                                                    Edit,
                                                                    currentUser,
                                                                  ) ||
                                                                  Method.hasPermission(
                                                                    Categories,
                                                                    Delete,
                                                                    currentUser,
                                                                  ) ? (
                                                                    <CustomSelectTable
                                                                      marginLeft={
                                                                        "-90px"
                                                                      }
                                                                      placeholder={
                                                                        <img
                                                                          width={
                                                                            24
                                                                          }
                                                                          height={
                                                                            5
                                                                          }
                                                                          src={
                                                                            ThreeDotMenu
                                                                          }
                                                                          alt=""
                                                                        />
                                                                      }
                                                                      backgroundColor="white"
                                                                      options={[
                                                                        ...(Method.hasPermission(
                                                                          Categories,
                                                                          Edit,
                                                                          currentUser,
                                                                        )
                                                                          ? [
                                                                              {
                                                                                label:
                                                                                  (
                                                                                    <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                                                      Edit
                                                                                      details
                                                                                    </button>
                                                                                  ),
                                                                                value: 1,
                                                                              },
                                                                            ]
                                                                          : []),
                                                                        ...(Method.hasPermission(
                                                                          Categories,
                                                                          Delete,
                                                                          currentUser,
                                                                        )
                                                                          ? [
                                                                              {
                                                                                label:
                                                                                  (
                                                                                    <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4">
                                                                                      Delete
                                                                                      details
                                                                                    </button>
                                                                                  ),
                                                                                value: 2,
                                                                              },
                                                                            ]
                                                                          : []),
                                                                      ]}
                                                                      show={
                                                                        groupVal._id ===
                                                                        groupId
                                                                      }
                                                                      onMenuClose={() => {
                                                                        onGroupMenuClose();
                                                                      }}
                                                                      openMenuOnClick={() => {
                                                                        openGroupMenuOnClick(
                                                                          groupVal._id,
                                                                        );
                                                                      }}
                                                                      onMenuOpen={() => {
                                                                        onGroupMenuOpen(
                                                                          groupVal._id,
                                                                        );
                                                                      }}
                                                                      onChange={(
                                                                        event: any,
                                                                      ) => {
                                                                        handleGroupOption(
                                                                          event,
                                                                          groupVal,
                                                                          primaryVal,
                                                                          subVal,
                                                                        );
                                                                      }}
                                                                      isOptionDisabled={(
                                                                        option: any,
                                                                      ) =>
                                                                        option.value ===
                                                                        3
                                                                      }
                                                                    />
                                                                  ) : (
                                                                    <></>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                          {/* {groupIndex ===
                                                        subVal.categories
                                                          .length -
                                                          1 ? (
                                                          <></>
                                                        ) : (
                                                          <div className="separator "></div>
                                                        )} */}
                                                        </>
                                                      );
                                                    },
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      );
                                    },
                                  )}
                              </div>
                            </div>
                          </>
                        );
                      })
                    ) : (
                      <div className="d-flex justify-content-center m-4">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </>
            ) : (
              <></>
            )}
          </>
        )}
      </div>
    </>
  );
};
export default AllCategories;
